import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Home, TrendingUp, Gem, Compass } from "lucide-react";
import { useApp } from "@/context/AppContext";

const PURPOSES = [
  { key: "Living", icon: Home, desc: "A home for your life" },
  { key: "Investment", icon: TrendingUp, desc: "Real estate as an asset" },
  { key: "Luxury", icon: Gem, desc: "A different standard" },
  { key: "Exploring", icon: Compass, desc: "Still forming a view" },
];
const TYPES = ["Apartment", "Villa", "Plot", "Commercial", "Other"];
const BUDGETS = ["Under ₹80L", "₹80L – 1.2 Cr", "₹1.2 – 2 Cr", "₹2 – 4 Cr", "₹4 Cr +"];
const CONFIGS = ["2 BHK", "3 BHK", "3.5 BHK", "4 BHK", "5 BHK", "Villa", "Plot"];
const STATUSES = ["Ready to Move", "Under Construction", "Pre-Launch", "Any"];
const TIMELINES = ["0–3 months", "3–6 months", "6–12 months", "Just exploring"];
const LOCATIONS = ["Whitefield", "Sarjapur Road", "HSR Layout", "Hebbal", "Thanisandra", "Devanahalli", "Electronic City", "Kanakapura Road", "Bellandur", "Bannerghatta Road"];
const PRIORITIES = ["Connectivity", "Lifestyle", "Appreciation Potential", "Rental Potential", "Workplace", "Schools", "Developer", "Amenities", "Space", "Low Density", "Ready Possession"];
const CONTACT_METHODS = ["Phone", "WhatsApp", "Email"];
const TIMES = ["Morning", "Afternoon", "Evening"];

const STEPS = ["Purpose", "Property", "Preferences", "Priorities", "Contact"];

export default function Request() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { addLead } = useApp();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    purpose: params.get("purpose") || "",
    propertyType: "", budget: "", locations: [], configuration: "", status: "", timeline: "",
    priorities: [], name: "", phone: "", email: "",
    contactMethod: "Phone", preferredTime: "Morning", notes: "",
  });
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const validateStep = () => {
    const e = {};
    if (step === 0 && !form.purpose) e.purpose = "Select a purpose";
    if (step === 1 && !form.propertyType) e.propertyType = "Select a property type";
    if (step === 4) {
      if (!form.name.trim()) e.name = "Name required";
      if (!/^[+\d][\d\s-]{7,}$/.test(form.phone)) e.phone = "Valid phone required";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Valid email required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) { toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    try {
      const leadId = await addLead({
        name: form.name, phone: form.phone, email: form.email, purpose: form.purpose,
        propertyType: form.propertyType, budget: form.budget || "Not specified", locations: form.locations,
        configuration: form.configuration, timeline: form.timeline || "Not specified", status: form.status,
        contactMethod: form.contactMethod, preferredTime: form.preferredTime,
        priorities: form.priorities, notes: form.notes, source: "Website",
      });
      setDone(leadId);
      toast.success("Requirement received — perspective in progress.");
    } catch (e) {
      toast.error("Could not submit right now. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="container-arch flex min-h-[80vh] flex-col items-center justify-center pt-24 text-center">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex h-20 w-20 items-center justify-center rounded-full" style={{ background: "linear-gradient(135deg,#9ED3B8,#0F4A36)" }}>
          <Check className="h-9 w-9 text-[#071711]" />
        </motion.div>
        <h1 className="mt-8 font-serif text-4xl font-light text-[var(--text-primary)]">We have your perspective.</h1>
        <p className="mt-3 max-w-md text-[var(--text-muted)]">Your requirement has been received. An Archedia advisor will begin curating shortly.</p>
        <div className="mt-6 rounded-full border border-[var(--accent-gold)] px-5 py-2 font-mono text-sm text-[var(--accent-gold)]" data-testid="lead-id-confirmation">Lead ID: {done}</div>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/properties" className="btn-outline-gold">Explore Properties</Link>
        </div>
      </div>
    );
  }

  const chip = (label, active, onClick, testid) => (
    <button type="button" key={label} onClick={onClick} data-testid={testid} className={`rounded-full border px-4 py-2 text-sm transition ${active ? "border-[var(--accent-gold)] bg-[var(--accent-a10)] text-[var(--accent-gold)]" : "border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--accent-gold)]"}`}>{label}</button>
  );
  const inputCls = "w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-4 py-3 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]";

  return (
    <div className="container-arch pt-28 pb-24">
      <div className="mx-auto max-w-3xl">
        <div className="overline">Requirement Journey</div>
        <h1 className="mt-3 font-serif text-4xl font-light text-[var(--text-primary)] sm:text-5xl">Tell Us What You're Looking to Explore.</h1>

        {/* progress */}
        <div className="mt-10 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 flex-col gap-2">
              <div className={`h-1 rounded-full transition-all duration-500 ${i <= step ? "bg-[var(--accent-gold)]" : "bg-[var(--border-subtle)]"}`} />
              <span className={`text-[11px] ${i === step ? "text-[var(--accent-gold)]" : "text-[var(--text-muted)]"}`}>{s}</span>
            </div>
          ))}
        </div>

        <div className="arch-card mt-8 p-6 sm:p-8" data-testid="request-form">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              {step === 0 && (
                <div>
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">Why are you buying?</h3>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {PURPOSES.map((p) => {
                      const Icon = p.icon;
                      return (
                        <button key={p.key} type="button" onClick={() => set("purpose", p.key)} data-testid={`purpose-${p.key.toLowerCase()}`} className={`flex items-start gap-4 rounded-xl border p-5 text-left transition ${form.purpose === p.key ? "border-[var(--accent-gold)] bg-[var(--accent-a5)]" : "border-[var(--border-subtle)] hover:border-[var(--accent-a50)]"}`}>
                          <Icon className="h-6 w-6 text-[var(--accent-gold)]" />
                          <div><div className="font-serif text-lg text-[var(--text-primary)]">{p.key}</div><div className="text-sm text-[var(--text-muted)]">{p.desc}</div></div>
                        </button>
                      );
                    })}
                  </div>
                  {errors.purpose && <p className="mt-3 text-xs text-red-500">{errors.purpose}</p>}
                </div>
              )}
              {step === 1 && (
                <div>
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">What type of property?</h3>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {TYPES.map((t) => chip(t, form.propertyType === t, () => set("propertyType", t), `type-${t.toLowerCase()}`))}
                  </div>
                  {errors.propertyType && <p className="mt-3 text-xs text-red-500">{errors.propertyType}</p>}
                </div>
              )}
              {step === 2 && (
                <div className="space-y-6">
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">Your preferences</h3>
                  <div><div className="mb-2 text-sm font-medium text-[var(--text-primary)]">Budget</div><div className="flex flex-wrap gap-2">{BUDGETS.map((b) => chip(b, form.budget === b, () => set("budget", b)))}</div></div>
                  <div><div className="mb-2 text-sm font-medium text-[var(--text-primary)]">Preferred Locations</div><div className="flex flex-wrap gap-2">{LOCATIONS.map((l) => chip(l, form.locations.includes(l), () => toggleArr("locations", l)))}</div></div>
                  <div><div className="mb-2 text-sm font-medium text-[var(--text-primary)]">Configuration</div><div className="flex flex-wrap gap-2">{CONFIGS.map((c) => chip(c, form.configuration === c, () => set("configuration", c)))}</div></div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><div className="mb-2 text-sm font-medium text-[var(--text-primary)]">Property Status</div><select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}><option value="">Select</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
                    <div><div className="mb-2 text-sm font-medium text-[var(--text-primary)]">Timeline</div><select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className={inputCls}><option value="">Select</option>{TIMELINES.map((t) => <option key={t}>{t}</option>)}</select></div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div>
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">What matters most?</h3>
                  <p className="mt-1 text-sm text-[var(--text-muted)]">Choose the priorities that define the right property for you.</p>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {PRIORITIES.map((p) => chip(p, form.priorities.includes(p), () => toggleArr("priorities", p), `priority-${p.toLowerCase().replace(/\s+/g, "-")}`))}
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className="space-y-5">
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">How can we reach you?</h3>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div><label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Name</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="req-name" className={inputCls} />{errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}</div>
                    <div><label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Phone</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="req-phone" className={inputCls} />{errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}</div>
                  </div>
                  <div><label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Email</label><input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="req-email" className={inputCls} />{errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}</div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div><label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Preferred Contact</label><select value={form.contactMethod} onChange={(e) => set("contactMethod", e.target.value)} className={inputCls}>{CONTACT_METHODS.map((m) => <option key={m}>{m}</option>)}</select></div>
                    <div><label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Preferred Time</label><select value={form.preferredTime} onChange={(e) => set("preferredTime", e.target.value)} className={inputCls}>{TIMES.map((t) => <option key={t}>{t}</option>)}</select></div>
                  </div>
                  <div><label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Additional Requirement</label><textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="req-notes" className={inputCls} /></div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between border-t border-[var(--border-subtle)] pt-6">
            <button onClick={back} disabled={step === 0} data-testid="req-back" className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} data-testid="req-next" className="btn-gold">Continue <ArrowRight className="h-4 w-4" /></button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="req-submit" className="btn-gold disabled:opacity-60">{submitting ? "Submitting…" : "Submit Requirement"} <Check className="h-4 w-4" /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
